import { type DeeplinkElementType } from '@/pages/menuEntries/MenuEntry.type'
import { create } from '@/shared/utils/zustandVue'

import type { PreviewGrabData } from '../types/applicationData.type'

// Params bag forwarded to the mobile preview's `params` field. Each preview
// page has its own shape (PreviewPageParams<T> on the mobile side), but the
// editor builds these from form state and serializes them as a plain bag —
// `Record<string, unknown>` keeps that flexibility without falling back to
// `any`, so e.g. a `DateTimeMs` mistakenly stored as a string surfaces when
// the bag is consumed.
export type PreviewPageData = Record<string, unknown>

class PreviewState {
  page: DeeplinkElementType | null = null
  pageData: PreviewPageData | null = null
  resyncPage: DeeplinkElementType | null = null
  resyncPageData: PreviewPageData | null = null
  packedData: PreviewGrabData | null = null
  isOpen = false
  resyncToHome = false
  contentSaving = false
}

type PreviewStore = PreviewState & {
  setPreview: (payload: Partial<PreviewState>) => void
  resetNavigation: () => void
  startContentSave: () => void
  finishContentSave: () => void
  setPackedData: (packedData: PreviewGrabData) => void
  setOpen: (isOpen: boolean) => void
  toggleOpen: () => void
  openPreview: () => void
  closePreview: () => void
}

/**
 * Preview store — idiomatic zustand-vue.
 *
 * Controls the mobile preview sidebar: which page to show, its data bag,
 * the packed grab data, and open/close state. `setPreview` merges a partial
 * payload into state (replacing the old `setPreview` mutation).
 */
export const usePreviewStore = create<PreviewStore>((set) => ({
  ...new PreviewState(),
  setPreview: (payload) => {
    set(payload)
  },
  resetNavigation: () => {
    set({ resyncPage: null, resyncPageData: null, resyncToHome: true })
  },
  startContentSave: () => {
    set({ contentSaving: true })
  },
  finishContentSave: () => {
    set({ contentSaving: false })
  },
  setPackedData: (packedData) => {
    set({ packedData })
  },
  setOpen: (isOpen) => {
    set({ isOpen })
  },
  toggleOpen: () => {
    set((state) => ({ isOpen: !state.isOpen }))
  },
  openPreview: () => {
    set({ isOpen: true })
  },
  closePreview: () => {
    set({ isOpen: false })
  },
}))

/**
 * Flags the preview content as being saved while `task` runs. The preview
 * decides how to react, emitters only announce the save.
 */
export async function withContentSave(
  task: () => Promise<void>,
): Promise<void> {
  usePreviewStore.getState().startContentSave()
  try {
    await task()
  } finally {
    usePreviewStore.getState().finishContentSave()
  }
}
